import axios from 'axios';

let API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:80/api';
if (typeof window !== 'undefined' && window.location.hostname.includes('surgeryjourney')) {
  API_BASE_URL = 'https://api.surgeryjourney.com/api';
}
console.log("api base url", API_BASE_URL);

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: function (status) {
    return status >= 200 && status < 300;
  }
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const api = {
  getNotifications: async () => {
    const response = await axiosInstance.get("/notifications")
    return response.data;
  },
  deleteSurgery: async (surgery) => {
    const response = await axiosInstance.delete(`/review/${surgery}`)
    return response.data;
  },
  deleteUpdate: async (update) => {
    const response = await axiosInstance.delete(`/update/${update}`)
    return response.data;
  },
  deleteDiaryEntry: async (entry) => {
    const response = await axiosInstance.delete(`/diary/entry/${entry}`)
    return response.data
  },

  getSurgeonsByState: async (stateCode) => {
    const response = await axiosInstance.get(`/surgeons/${stateCode}`);
    return response.data;
  },

  getSurgeryTypes: async () => {
    const response = await axiosInstance.get('/surgery-types');
    return response.data;
  },

  getStates: async () => {
    const response = await axiosInstance.get('/states');
    return response.data;
  },

  getUsers: async () => {
    const response = await axiosInstance.get('/users');
    return response.data;
  },

  getReviews: async (surgery, user) => {
    let params = {}
    if (surgery) {
      params.surgery = surgery
    }
    if (user) {
      params.user = user
    }
    const response = await axiosInstance(
      {
        method: "get",
        url:"/reviews",
        params
      }
    );
    return response.data;
  },

  getDiary: async (id) => {
    const response = await axiosInstance.get(`/diary/${id}`);
    return response.data;
  },

  getReview: async (id) => {
    const reviews = await api.getReviews(id, null)
    if (reviews?.length !== 1) {
      throw new Error("Not Found")
    }
    return reviews[0];
  },

  getUpdate: async (id) => {
    const response = await axiosInstance.get(`/updates/${id}`);
    return response.data;
  },

  readComment: async (id) => {
    const response = await axiosInstance.post(`/comment/${id}/read`);
    return response.data
  },

  readReply: async (id) => {
    const response = await axiosInstance.post(`/reply/${id}/read`);
    return response.data
  },

  readAllNotifications: async () => {
    const response = await axiosInstance.post('/notification/all/read');
    return response.data
  },

  saveReview: async (reviewData) => {
    if (reviewData.id) {
      const response = await axiosInstance.put(`/review`, reviewData)
      return response.data;
    }
    const response = await axiosInstance.post('/review', reviewData);
    return response.data;
  },

  saveDiary: async (diary) => {
    if (diary.id) {
      const response = await axiosInstance.put('/diary', diary)
      return response.data
    }
    const response = await axiosInstance.post('/diary', diary);
    return response.data;
  },

  saveDiaryEntry: async (payload) => {
    if (payload.id) {
      const response = await axiosInstance.put('/diary/entry', payload)
      return response.data
    }
    const response = await axiosInstance.post('/diary/entry', payload)
    return response.data
  },

  createReviewUpdate: async (updateData) => {
    const response = await axiosInstance.post('/review/update', updateData);
    return response.data;
  },

  editUpdate: async (id, updateData) => {
    const response = await axiosInstance.put(`/update/${id}`, updateData);
    return response.data;
  },

  // Search
  searchDoctors: async (state, query, page = 1, pageSize = 10) => {
    const response = await axiosInstance.get(
      `/doctors/search?state=${encodeURIComponent(state)}&q=${encodeURIComponent(query)}&page=${page}&pageSize=${pageSize}`
    );
    return response.data;
  },

  // Auth
  validateFacebookAuth: async (userID, accessToken) => {
    const response = await axiosInstance.post('/facebook/validate', {
      userID,
      accessToken,
    });
    return response.data;
  },

  exchangeCode: async (code) => {
    const response = await axiosInstance.post('/facebook/code', {
      code
    })
    return response.data;
  },

  validateEmailAuth: async (email, password) => {
    const response = await axiosInstance.post('/email/validate', {
      email,
      password,
    })
    return response.data;
  },

  // TODO: FACEBOOKUSERID IS NOT NEEDED HERE
  createPatient: async (facebookUserID, username, accessToken, image, background) => {
    const response = await axiosInstance.post('/patient', {
      facebook_user_id: facebookUserID,
      username,
      access_token: accessToken,
      image,
      background
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await axiosInstance.get('/patient/me');
    return response.data;
  },

  getSurgeon: async (id) => {
    const response = await axiosInstance.get(`/surgeons/${id}`);
    return response.data;
  },

  getSurgeonsByState: async (state) => {
    const response = await axiosInstance.get(`/surgeons/state/${state}`);
    return response.data;
  },

  createComment: async (comment) => {
    const response = await axiosInstance.post('/comment', comment);
    return response.data;
  },

  getComments: async (reviewId) => {
    const response = await fetch(`${API_BASE_URL}/comments/${reviewId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch comments');
    }
    return response.json();
  },

  async isCodeValid(token) {
    const response = await axiosInstance.post('/email/valid', { token });
    return response.data;
  },

  async sendEmailVerification(email) {
    const response = await axiosInstance.post('/email/verify', { email });
    return response.data;
  },

  async verifyEmail(token) {
    const response = await axiosInstance.post('/email/valid', { token });
    return response.data;
  },

  createPassword: async (token, password, username, background) => {
    const response = await axiosInstance.post('/email/create-password', { token, password, username, background });
    return response.data;
  },

  uploadImagingFile: async (file, expectedHash, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (expectedHash) {
      formData.append('expectedHash', expectedHash);
    }
    const response = await axiosInstance.post('/imaging/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response.data;
  },

  uploadImagingThumbnail: async (blob, filename, onUploadProgress) => {
    const formData = new FormData();
    formData.append('thumbnail', blob, filename);
    const response = await axiosInstance.post('/imaging/thumbnail', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response.data;
  },

  saveImagingMetadata: async (metadata) => {
    const response = await axiosInstance.post('/imaging/metadata', metadata);
    return response.data;
  },

  getImagingFolders: async () => {
    const response = await axiosInstance.get('/imaging/folders');
    return response.data;
  },

  updateImagingTags: async (studyUID, tags) => {
    const response = await axiosInstance.put(
      `/imaging/folder/${encodeURIComponent(studyUID)}/tags`,
      { tags }
    );
    return response.data;
  },

  deleteImagingFolder: async (studyUID) => {
    const response = await axiosInstance.delete(`/imaging/folder/${encodeURIComponent(studyUID)}`);
    return response.data;
  },

  downloadImagingFile: async (key, onDownloadProgress) => {
    const response = await axiosInstance.get('/imaging/file', {
      params: { key },
      responseType: 'blob',
      onDownloadProgress,
    });
    return response.data;
  },

  createImagingShare: async ({ studyInstanceUIDs, expiresAt }) => {
    const response = await axiosInstance.post('/imaging/shares', {
      studyInstanceUIDs,
      expiresAt: expiresAt || null,
    });
    return response.data;
  },

  getImagingShares: async () => {
    const response = await axiosInstance.get('/imaging/shares');
    return response.data;
  },

  updateImagingShare: async (shareId, { studyInstanceUIDs, expiresAt }) => {
    const response = await axiosInstance.put(`/imaging/shares/${encodeURIComponent(shareId)}`, {
      studyInstanceUIDs,
      expiresAt: expiresAt || null,
    });
    return response.data;
  },

  deleteImagingShare: async (shareId) => {
    const response = await axiosInstance.delete(`/imaging/shares/${encodeURIComponent(shareId)}`);
    return response.data;
  },

  getSharedImaging: async (token) => {
    const response = await axiosInstance.get(`/share/${encodeURIComponent(token)}`);
    return response.data;
  },

  downloadSharedImagingFile: async (token, key, onDownloadProgress) => {
    const response = await axiosInstance.get(`/share/${encodeURIComponent(token)}/file`, {
      params: { key },
      responseType: 'blob',
      onDownloadProgress,
    });
    return response.data;
  },
};